import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./UIFieldComponentsLibrary.module.css";


import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaAngleDown,
} from "react-icons/fa";
import LoaderComp from "../../LoaderComp";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

// ----------------------------------------------------------------
// The allowed types on the backend. Kept in lockstep with
// users.py::UI_FIELD_COMPONENT_ALLOWED_TYPES.
// ----------------------------------------------------------------
const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "tel", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "textarea", label: "Text Area" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "datetime-local", label: "Date & Time" },
  { value: "range", label: "Range Slider" },
  { value: "toggle", label: "Toggle (Yes/No)" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Radio Buttons" },
  { value: "checkbox", label: "Checkboxes" },
  { value: "daterange", label: "Date Range" },
];

const OPTION_TYPES = new Set(["select", "radio", "checkbox"]);
const NUMERIC_TYPES = new Set(["number", "range"]);

const emptyDraft = () => ({
  component_id: "",
  name: "",
  type: "text",
  options: [],
  default_placeholder: "",
  min: "",
  max: "",
  step: "",
});

const UIFieldComponentsLibrary = ({ appData }) => {
  const [components, setComponents] = useState([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showEditor, setShowEditor] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [draftError, setDraftError] = useState("");
  const [optionDraft, setOptionDraft] = useState({ value: "", label: "" });

  const appName = appData?.app_name;

  // ----------------------------------------------------------------
  // Auth-aware fetch helper. Every call goes through the
  // Authorization header with the current token from localStorage.
  // ----------------------------------------------------------------
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No token found in localStorage");
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
      ...(options.headers || {}),
    };
    return fetch(url, { ...options, headers });
  }, []);

  const region = appData?.region?.toLowerCase() || "us";
  const apiBase = `https://${region}.autobot2.${backend_api_base_url}`;

  // ----------------------------------------------------------------
  // Load the full library for this app.
  // ----------------------------------------------------------------
  const fetchLibrary = useCallback(async () => {
    if (!appName) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(
        `${apiBase}/ui_field_component?app_name=${encodeURIComponent(appName)}`,
      );
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Failed to load library: ${resp.status} ${text}`);
      }
      const data = await resp.json();
      setComponents(Array.isArray(data.components) ? data.components : []);
      setVersion(Number.isFinite(data.version) ? data.version : 0);
    } catch (err) {
      console.error("[UIFieldComponentsLibrary] load failed:", err);
      setError(err.message);
      setComponents([]);
    } finally {
      setLoading(false);
    }
  }, [appName, apiBase, authFetch]);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  // ----------------------------------------------------------------
  // Save (create or update) a component.
  // ----------------------------------------------------------------
  const handleSave = async () => {
    setDraftError("");
    if (!draft.name.trim()) {
      setDraftError("Name is required.");
      return;
    }
    if (OPTION_TYPES.has(draft.type) && draft.options.length === 0) {
      setDraftError(`Type "${draft.type}" requires at least one option.`);
      return;
    }
    if (draft.type === "range") {
      if (draft.min === "" || draft.max === "") {
        setDraftError("Range requires both min and max.");
        return;
      }
      if (Number(draft.min) >= Number(draft.max)) {
        setDraftError("Range min must be strictly less than max.");
        return;
      }
    }

    const payload = {
      app_name: appName,
      name: draft.name.trim(),
      type: draft.type,
    };
    if (draft.component_id) payload.component_id = draft.component_id;

    if (OPTION_TYPES.has(draft.type)) {
      payload.options = draft.options.map((o) => ({
        value: String(o.value || "").trim(),
        label: String(o.label || "").trim(),
      }));
    }
    if (draft.default_placeholder !== "") {
      payload.default_placeholder = draft.default_placeholder;
    }
    if (NUMERIC_TYPES.has(draft.type)) {
      if (draft.min !== "") payload.min = Number(draft.min);
      if (draft.max !== "") payload.max = Number(draft.max);
      if (draft.step !== "") payload.step = Number(draft.step);
    }

    setSaving(true);
    try {
      const resp = await authFetch(`${apiBase}/ui_field_component`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || data.success === false) {
        throw new Error(data.error || `Save failed (${resp.status})`);
      }
      setShowEditor(false);
      setDraft(emptyDraft());
      await fetchLibrary();
    } catch (err) {
      console.error("[UIFieldComponentsLibrary] save failed:", err);
      setDraftError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ----------------------------------------------------------------
  // Delete a component. The backend refuses if any form still
  // references it — we surface the full error verbatim.
  // ----------------------------------------------------------------
  const handleDelete = async (component) => {
    const ok = window.confirm(
      `Delete "${component.name}"?\n\n` +
        `This will be refused if any form in this app still references it.`,
    );
    if (!ok) return;

    try {
      const resp = await authFetch(
        `${apiBase}/ui_field_component?app_name=${encodeURIComponent(
          appName,
        )}&component_id=${encodeURIComponent(component.component_id)}`,
        { method: "DELETE" },
      );
      const data = await resp.json();
      if (!resp.ok || data.success === false) {
        alert(data.error || `Delete failed (${resp.status})`);
        return;
      }
      await fetchLibrary();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  // ----------------------------------------------------------------
  // Editor helpers
  // ----------------------------------------------------------------
  const openCreate = () => {
    setDraft(emptyDraft());
    setDraftError("");
    setOptionDraft({ value: "", label: "" });
    setShowEditor(true);
  };

  const openEdit = (component) => {
    setDraft({
      component_id: component.component_id,
      name: component.name || "",
      type: component.type || "text",
      options: Array.isArray(component.options)
        ? component.options.map((o) => ({ value: o.value, label: o.label }))
        : [],
      default_placeholder: component.default_placeholder || "",
      min: component.min !== undefined && component.min !== null ? String(component.min) : "",
      max: component.max !== undefined && component.max !== null ? String(component.max) : "",
      step: component.step !== undefined && component.step !== null ? String(component.step) : "",
    });
    setDraftError("");
    setOptionDraft({ value: "", label: "" });
    setShowEditor(true);
  };

  const addOption = () => {
    const value = optionDraft.value.trim();
    const label = optionDraft.label.trim();
    if (!value || !label) {
      setDraftError("Both value and label are required for an option.");
      return;
    }
    if (draft.options.some((o) => o.value === value)) {
      setDraftError(`Option value "${value}" already exists.`);
      return;
    }
    setDraft((d) => ({
      ...d,
      options: [...d.options, { value, label }],
    }));
    setOptionDraft({ value: "", label: "" });
    setDraftError("");
  };

  const removeOption = (idx) => {
    setDraft((d) => ({
      ...d,
      options: d.options.filter((_, i) => i !== idx),
    }));
  };

  const updateDraft = (patch) => {
    setDraft((d) => {
      const next = { ...d, ...patch };
      // Auto-cleanup when switching types.
      if (patch.type && !OPTION_TYPES.has(patch.type)) next.options = [];
      if (patch.type && !NUMERIC_TYPES.has(patch.type)) {
        next.min = "";
        next.max = "";
        next.step = "";
      }
      return next;
    });
    setDraftError("");
  };

  // ----------------------------------------------------------------
  // Derived flags for the editor
  // ----------------------------------------------------------------
  const isOptionType = OPTION_TYPES.has(draft.type);
  const isNumericType = NUMERIC_TYPES.has(draft.type);
  const isRange = draft.type === "range";

  const typeLabel = useMemo(() => {
    const t = FIELD_TYPES.find((f) => f.value === draft.type);
    return t ? t.label : draft.type;
  }, [draft.type]);

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  if (loading) {
    return (
      <div className={styles.libraryWrapper}>
        <div className={styles.loadingWrap}>
          <LoaderComp message="Loading UI field components" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.libraryWrapper}>
      <div className={styles.headerRow}>
        <div className={styles.titleSection}>
          <h2 className={styles.title}>UI Field Components Library</h2>
          <p className={styles.subtitle}>
            Reusable field definitions that can be imported into any lead form.
            Edit a component here and every unpinned form that uses it picks up
            the change on next render.
          </p>
          <span className={styles.versionChip}>
            Library version: {version}
          </span>
        </div>
        <button className={styles.primaryBtn} onClick={openCreate}>
          <FaPlus size={13} />
          New Component
        </button>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {components.length === 0 ? (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyTitle}>No components yet</h3>
          <p className={styles.emptyText}>
            Create your first UI field component — a Country Dropdown, a
            preferred Contact Method radio group, a Phone Number text field.
            Once created, you can import it into any lead form and only supply
            the label.
          </p>
          <button className={styles.primaryBtn} onClick={openCreate}>
            <FaPlus size={13} />
            Create Component
          </button>
        </div>
      ) : (
        <div className={styles.grid}>
          {components.map((c) => (
            <div key={c.component_id} className={styles.card}>
              <div className={styles.cardHeader}>
                <h4 className={styles.cardName}>{c.name}</h4>
                <span className={styles.typeBadge}>{c.type}</span>
              </div>

              <div className={styles.cardMeta}>
                {c.default_placeholder && (
                  <div className={styles.cardMetaRow}>
                    <span className={styles.cardMetaLabel}>Placeholder:</span>
                    <span>{c.default_placeholder}</span>
                  </div>
                )}

                {isOptionTypeFor(c.type) && Array.isArray(c.options) && (
                  <div className={styles.cardMetaRow}>
                    <span className={styles.cardMetaLabel}>Options:</span>
                    <div className={styles.optionPreview}>
                      {c.options.slice(0, 3).map((o, i) => (
                        <span key={i} className={styles.optionChip} title={o.label}>
                          {o.label}
                        </span>
                      ))}
                      {c.options.length > 3 && (
                        <span className={styles.optionChipMore}>
                          +{c.options.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {c.type === "range" && (
                  <div className={styles.cardMetaRow}>
                    <span className={styles.cardMetaLabel}>Range:</span>
                    <span>
                      min {c.min} → max {c.max} (step {c.step})
                    </span>
                  </div>
                )}

                <div className={styles.cardMetaRow}>
                  <span className={styles.cardMetaLabel}>ID:</span>
                  <span style={{ fontFamily: "monospace", fontSize: 11 }}>
                    {c.component_id}
                  </span>
                </div>
              </div>

              <div className={styles.cardActions}>
                <button
                  className={styles.iconBtn}
                  onClick={() => openEdit(c)}
                >
                  <FaEdit size={11} />
                  Edit
                </button>
                <button
                  className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                  onClick={() => handleDelete(c)}
                >
                  <FaTrash size={11} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditor && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {draft.component_id ? "Edit Component" : "New Component"}
              </h3>
              <button
                className={styles.modalClose}
                onClick={() => setShowEditor(false)}
              >
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  Name<span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  value={draft.name}
                  onChange={(e) => updateDraft({ name: e.target.value })}
                  placeholder="e.g. Country Dropdown"
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  Type<span className={styles.required}>*</span>
                </label>
                <select
                  className={styles.fieldSelect}
                  value={draft.type}
                  onChange={(e) => updateDraft({ type: e.target.value })}
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <p className={styles.helperText}>
                  The field type is fixed once the component is imported into
                  a form.
                </p>
              </div>

              {isOptionType && (
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Options<span className={styles.required}>*</span>
                  </label>
                  <p className={styles.helperText}>
                    Each option needs a value (stored in lead data) and a label
                    (shown to the end user).
                  </p>

                  {draft.options.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {draft.options.map((o, i) => (
                        <div key={i} className={styles.optionRow}>
                          <input
                            type="text"
                            className={styles.fieldInput}
                            value={o.value}
                            disabled
                            style={{ flex: "0 0 140px" }}
                          />
                          <input
                            type="text"
                            className={styles.fieldInput}
                            value={o.label}
                            disabled
                          />
                          <button
                            type="button"
                            className={styles.optionRowRemove}
                            onClick={() => removeOption(i)}
                            title="Remove option"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className={styles.optionRow} style={{ marginTop: 8 }}>
                    <input
                      type="text"
                      className={styles.fieldInput}
                      placeholder="value (e.g. IN)"
                      value={optionDraft.value}
                      onChange={(e) =>
                        setOptionDraft((d) => ({ ...d, value: e.target.value }))
                      }
                      style={{ flex: "0 0 140px" }}
                    />
                    <input
                      type="text"
                      className={styles.fieldInput}
                      placeholder="label (e.g. India)"
                      value={optionDraft.label}
                      onChange={(e) =>
                        setOptionDraft((d) => ({ ...d, label: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addOption();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={addOption}
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  Default Placeholder
                </label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  value={draft.default_placeholder}
                  onChange={(e) =>
                    updateDraft({ default_placeholder: e.target.value })
                  }
                  placeholder="Optional. The form can override this per-import."
                />
              </div>

              {isNumericType && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 10,
                  }}
                >
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                      Min{isRange && <span className={styles.required}>*</span>}
                    </label>
                    <input
                      type="number"
                      className={styles.fieldInput}
                      value={draft.min}
                      onChange={(e) => updateDraft({ min: e.target.value })}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                      Max{isRange && <span className={styles.required}>*</span>}
                    </label>
                    <input
                      type="number"
                      className={styles.fieldInput}
                      value={draft.max}
                      onChange={(e) => updateDraft({ max: e.target.value })}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Step</label>
                    <input
                      type="number"
                      className={styles.fieldInput}
                      value={draft.step}
                      onChange={(e) => updateDraft({ step: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {draftError && <p className={styles.errorText}>{draftError}</p>}
            </div>

            <div className={styles.modalActions}>
              <button
                className={styles.cancelBtn}
                onClick={() => setShowEditor(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : draft.component_id ? "Save Changes" : "Create Component"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Small helper kept outside the component so it doesn't rebuild
// the set on every render.
function isOptionTypeFor(type) {
  return OPTION_TYPES.has(type);
}

export default UIFieldComponentsLibrary;